import React, { useRef, useEffect } from 'react';
import { ChatMessage as ChatMessageType } from '../types/interview';
import { sanitizeHTML, formatTimestamp } from '../utils/helpers';
import { WatermarkService } from '../services/watermarkService';
import styles from '../styles/Interview.module.css';
import RateExperience from './RateExperience';
import { useInterviewStore } from '../stores';

interface ChatMessageProps {
  message: ChatMessageType;
}

const ChatMessage: React.FC<ChatMessageProps> = ({ message }) => {
  const session = useInterviewStore(state => state.session);

  // Prepare human-readable timestamp for tooltip
  const tooltip = message.timestamp ? formatTimestamp(message.timestamp.getTime()) : undefined;

  // Debug logging for acknowledgment messages
  if (message.content.includes('Thank you for sharing') || message.content.includes('appreciate')) {
    console.log('ChatMessage: Rendering potential acknowledgment message:', {
      id: message.id,
      content: message.content.substring(0, 50) + '...',
      type: message.type,
      timestamp: message.timestamp
    });
  }

  // Debug logging for explanation messages
  if (message.content.includes('move on') || message.content.includes('continue') || message.content.includes('explore')) {
    console.log('ChatMessage: Rendering potential explanation message:', {
      id: message.id,
      content: message.content.substring(0, 50) + '...',
      type: message.type,
      timestamp: message.timestamp
    });
  }

  const getMessageClasses = () => {
    let classes = `${styles.message} ${styles[message.type]}`;

    if (message.is_referral) {
      classes += ` ${styles.referral}`;
    }

    if (message.type === 'status' && message.level) {
      // Status messages are styled differently (centered, no bubble)
      // We might want to return just the status classes here if we weren't reusing this function
      // But for now, let's keep it consistent
      classes += ` ${styles[message.level]}`;
    }

    // Add visual indicator for acknowledgment messages
    if (message.is_acknowledgment) {
      classes += ` ${styles.acknowledgment || ''}`;
    }

    // Add visual indicator for explanation messages
    if (message.is_explanation) {
      classes += ` ${styles.explanation || ''}`;
    }

    return classes;
  };

  const contentRef = useRef<HTMLDivElement>(null);

  // Intercept copy events for questions to preserve watermark in clipboard
  useEffect(() => {
    if (!message.is_question || !contentRef.current) {
      return;
    }

    const handleCopy = (e: ClipboardEvent) => {
      // When user copies a question, ensure watermarked version is copied
      const originalContent = message.content;
      if (originalContent && e.clipboardData) {
        e.preventDefault();
        e.clipboardData.setData('text/plain', originalContent);
      }
    };

    const element = contentRef.current;
    element.addEventListener('copy', handleCopy);

    return () => {
      element.removeEventListener('copy', handleCopy);
    };
  }, [message.is_question, message.content]);

  const renderContent = () => {
    if (message.is_guardrail) {
      // Strip redundant prefix from content if banner is shown
      const prefixToStrip = 'System check — not a technical issue.';
      let cleanedContent = message.content.trim();
      if (cleanedContent.startsWith(prefixToStrip)) {
        cleanedContent = cleanedContent.substring(prefixToStrip.length).trim();
      }

      return (
        <div className={styles.messageContent}>
          <div className={styles.guardrailHeader}>
            <span className={styles.guardrailIcon} aria-hidden>⚠️</span>
            <strong>System check — not a technical issue.</strong>
          </div>
          <div>{cleanedContent}</div>
        </div>
      );
    }

    if (message.isRatingRequest) {
      if (session.hasRated) {
        return (
          <div className={styles.messageContent}>
            Thank you for your feedback!
          </div>
        );
      }

      return (
        <div className={styles.messageContent}>
          {message.content}
          <RateExperience />
        </div>
      );
    }

    if (message.is_html) {
      // Sanitize HTML content before rendering
      const sanitizedContent = sanitizeHTML(message.content);
      return (
        <div
          className={styles.messageContent}
          dangerouslySetInnerHTML={{ __html: sanitizedContent }}
        />
      );
    }

    // Questions carry an explicit flag. Avoid content heuristics that can
    // misclassify ordinary candidate or assistant messages containing numbers.
    const isQuestionMessage = Boolean(message.is_question);
    
    if (isQuestionMessage) {
      const cleanContent = WatermarkService.stripWatermarks(message.content);
      return (
        <div 
          ref={contentRef}
          className={styles.messageContent}
          data-watermarked-text={message.content} // Store original for copy
        >
          {cleanContent}
        </div>
      );
    }

    return (
      <div className={styles.messageContent}>
        {message.content}
      </div>
    );
  };

  // Don't render empty messages
  if (!message.content.trim()) {
    console.log('ChatMessage: Skipping empty message:', {
      id: message.id,
      content: `"${message.content}"`,
      type: message.type
    });
    return null;
  }

  // Render Logic
  const isAi = message.type === 'ai';
  const isStatus = message.type === 'status';

  // For status messages, keep original layout (centered, full width)
  if (isStatus) {
    return (
      <div className={getMessageClasses()} title={tooltip}>
        {renderContent()}
      </div>
    );
  }

  // For Chat Messages (AI/User), use the Row Layout with Avatars
  return (
    <div className={`${styles.messageRow} ${isAi ? styles.ai : styles.user}`}>
      {isAi && (
        <div className={styles.avatar}>
          <img src="/isa_portrait.png" alt="ISA Avatar" />
        </div>
      )}
      
      <div className={getMessageClasses()} title={tooltip}>
        {renderContent()}
      </div>
    </div>
  );
};

export default React.memo(ChatMessage);
